import appConfig from '@/config/appConfig.typed';
import analyticsEventProperties from '@/consts/analytics/analyticsEventProperties';
import analyticsEventValues from '@/consts/analytics/analyticsEventValues';
import gaEventNames from '@/consts/analytics/gaEventNames';
import {getEventNameByPromptType} from '@/consts/pushNotifications/pushNotificationsPromptTypes';

import {properties, toggleAnalytics, track} from './googleAnalytics';

const trackEnableNotificationsScreenView = () => {
    track({
        event: gaEventNames.NOTIFICATION_RECEIVED,
        properties: [properties.interactionType(analyticsEventValues.SCREEN_VIEWED)],
    });
};

const trackEnableNotificationsNextButtonClick = () => {
    track({
        event: gaEventNames.NOTIFICATION_RECEIVED,
        properties: [properties.interactionType(analyticsEventValues.NEXT_BUTTON_CLICKED)],
    });
};

const trackAllowPushNotifications = () => {
    track({
        event: gaEventNames.NOTIFICATION_RECEIVED,
        properties: [properties.interactionType(analyticsEventValues.NOTIFICATION_ALLOWED), properties.success(true)],
    });
};

const trackPushNotificationsSettingScreenViewed = (isEnabled) => {
    const settingValue = isEnabled ? 'Enabled' : 'Disabled';

    track({
        event: gaEventNames.NOTIFICATIONS_SETTINGS_SCREEN_VIEWED,
        properties: [
            {
                [analyticsEventProperties.GLOBAL_SETTING_VALUE]: settingValue,
            },
        ],
    });
};

const trackPushNotificationsSettingChanged = (isEnabled, isSuccess) => {
    const settingValue = isEnabled ? 'Enabled' : 'Disabled';

    track({
        event: gaEventNames.NOTIFICATIONS_SETTINGS_CHANGED,
        properties: [
            {
                [analyticsEventProperties.GLOBAL_SETTING_VALUE]: settingValue,
            },
            properties.success(isSuccess),
        ],
    });
};

const trackPushNotificationsPromptViewed = (promptType) => {
    track({
        event: gaEventNames.NOTIFICATIONS_PROMPT_VIEWED,
        properties: [
            {
                [analyticsEventProperties.TRIGGER]: getEventNameByPromptType(promptType),
            },
        ],
    });
};

const trackPushNotificationsSystemSettingsStepsViewed = () => {
    track({
        event: gaEventNames.NOTIFICATIONS_SYSTEM_SETTINGS_STEPS_VIEWED,
    });
};

const trackDisallowPushNotifications = () => {
    track({
        event: gaEventNames.NOTIFICATION_RECEIVED,
        properties: [
            properties.interactionType(analyticsEventValues.NOTIFICATION_DISALLOWED),
            properties.success(false),
        ],
    });
};

const trackHomeScreenView = () => {
    track({
        event: gaEventNames.HOME_SCREEN_VIEWED,
    });
};

const trackHomeTopBannerClick = (trackingValue) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.TOP_SECTION,
        properties: [
            properties.interactionType(analyticsEventValues.HOME_PAGE_TOP_BANNER_CLICKED),
            properties.trackingValue(trackingValue),
            properties.pageId(),
        ],
    });
};

const trackLoyaltyButtonClick = (trackingValue) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.LOYALTY_BUTTONS_BAR_CLICKED,
        properties: [properties.trackingValue(trackingValue), properties.pageId()],
    });
};

const trackHighlightsBannerClick = (trackingValue) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.HIGHLIGHT_BANNER_CLICKED,
        properties: [properties.trackingValue(trackingValue), properties.pageId()],
    });
};

const trackForYouTileClick = (trackingValue) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.FOR_YOU_TILE_OPENED,
        properties: [properties.trackingValue(trackingValue), properties.pageId()],
    });
};

const trackShopScreenView = () => {
    track({
        event: gaEventNames.SHOP_SECTION_VIEWED,
    });
};

const trackQuickOrderBannerClick = () => {
    track({
        event: gaEventNames.QUICK_ORDER_BANNER_CLICKED,
        properties: [properties.pageId()],
    });
};

const trackQuickOrderButtonClick = () => {
    track({
        event: gaEventNames.QUICK_ORDER_BUTTON_CLICKED,
        properties: [properties.pageId()],
    });
};

const trackYourDevicesButtonClick = (buttonType, deviceType) => {
    if (!buttonType || !deviceType) return;

    track({
        event: gaEventNames.YOUR_DEVICES_BUTTON_CLICKED,
        properties: [
            properties.pageId(),
            properties.deviceModel({deviceType}),
            {
                [analyticsEventProperties.BUTTON_TYPE]: buttonType,
            },
        ],
    });
};

const trackShopByProductBannerClick = (trackingValue) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.SHOP_BY_PRODUCT_CLICKED,
        properties: [properties.trackingValue(trackingValue), properties.pageId()],
    });
};

const trackDeviceScreenView = () => {
    track({
        event: gaEventNames.DEVICE_SECTION_VIEWED,
    });
};

const trackTryYourFirstIqosButtonClick = () => {
    track({
        event: gaEventNames.TRY_YOUR_FIRST_IQOS_BUTTON_CLICKED,
    });
};

const trackAboutMyDeviceScreenView = (deviceType, deviceSerialNumber) => {
    if (!deviceType || !deviceSerialNumber) return;

    track({
        event: gaEventNames.ABOUT_MY_DEVICE_SCREEN_VIEWED,
        properties: [properties.deviceModel({deviceType}), properties.deviceSerialNumber({deviceSerialNumber})],
    });
};

const trackTipsAndTutorialsScreenView = (deviceType, deviceSerialNumber) => {
    if (!deviceType || !deviceSerialNumber) return;

    track({
        event: gaEventNames.TIPS_AND_TUTORIALS_SCREEN_VIEWED,
        properties: [properties.deviceModel({deviceType}), properties.deviceSerialNumber({deviceSerialNumber})],
    });
};

const trackUnregisterDeviceClick = (deviceSerialNumber, deviceType, isSuccess) => {
    if (!deviceType || !deviceSerialNumber) return;

    track({
        event: gaEventNames.UNREGISTER_DEVICE_CLICKED,
        properties: [
            properties.deviceModel({deviceType}),
            properties.deviceSerialNumber({deviceSerialNumber}),
            properties.success(isSuccess),
        ],
    });
};

const trackInboxScreenView = () => {
    track({
        event: gaEventNames.INBOX_SCREEN_VIEWED,
    });
};

const trackInboxDetailsScreenView = (pushSource, campaignName) => {
    track({
        event: gaEventNames.INBOX_MESSAGE_OPENED,
        properties: [properties.pushSource(pushSource), properties.campaignName(campaignName)],
    });
};

const trackInboxMessageButtonClick = (pushSource, campaignName) => {
    track({
        event: gaEventNames.INBOX_ACTION_BUTTON_CLICKED,
        properties: [properties.pushSource(pushSource), properties.campaignName(campaignName)],
    });
};

const trackActionScreen = (pushSource) => {
    track({
        event: gaEventNames.ACTION_SCREEN_VIEWED,
        properties: [properties.pushSource(pushSource)],
    });
};

const trackPreferenceCenterScreenView = () => {
    track({
        event: gaEventNames.PREFERENCE_CENTER_VIEWED,
    });
};

const trackUpdateYourDataScreenView = () => {
    track({
        event: gaEventNames.PREFERENCE_CENTER_UPDATE_VIEWED,
    });
};

const trackContactUsScreenView = () => {
    track({
        event: gaEventNames.CONTACT_US_VIEWED,
    });
};

const trackShowProfileLinkClick = () => {
    track({
        event: gaEventNames.SHOW_PROFILE_CLICKED,
    });
};

const trackTermsOfUseClick = () => {
    track({
        event: gaEventNames.LEGAL_VIEWED,
        properties: [properties.legal(analyticsEventValues.TERMS_OF_USE)],
    });
};

const trackPrivacyNoticeClick = () => {
    track({
        event: gaEventNames.LEGAL_VIEWED,
        properties: [properties.legal(analyticsEventValues.PRIVACY_NOTICE)],
    });
};

const trackAttributionNoticeClick = () => {
    track({
        event: gaEventNames.LEGAL_VIEWED,
        properties: [properties.legal(analyticsEventValues.ATTRIBUTION_NOTICE)],
    });
};

const trackAddNewDeviceClick = () => {
    track({
        event: gaEventNames.ADD_NEW_DEVICE_CLICKED,
    });
};

const trackPairDeviceClick = (deviceType, deviceSerialNumber) => {
    track({
        event: gaEventNames.PAIR_DEVICE_CLICKED,
        properties: [properties.deviceModel({deviceType}), properties.deviceSerialNumber({deviceSerialNumber})],
    });
};

const trackChromeDownloadButtonClick = () => {
    track({
        event: gaEventNames.CHROME_DOWNLOAD_BUTTON_CLICKED,
    });
};

const trackRenameDeviceScreenView = () => {
    track({
        event: gaEventNames.RENAME_MY_DEVICE_VIEWED,
        properties: [properties.deviceModel({fromCurrent: true}), properties.deviceSerialNumber({fromCurrent: true})],
    });
};

const trackRenameDevice = () => {
    track({
        event: gaEventNames.DEVICE_RENAMED,
        properties: [properties.deviceModel({fromCurrent: true}), properties.deviceSerialNumber({fromCurrent: true})],
    });
};

const trackAdQuestionnaireSelectClick = (questionnairePage) => {
    track({
        event: gaEventNames.QUESTIONNAIRE_SELECT,
        properties: [
            properties.deviceModel({fromCurrent: true}),
            properties.deviceSerialNumber({fromCurrent: true}),
            properties.questionnairePage(questionnairePage),
        ],
    });
};

const trackAdQuestionnaireScroll = (questionnairePage) => {
    track({
        event: gaEventNames.QUESTIONNAIRE_SCROLL,
        properties: [
            properties.deviceModel({fromCurrent: true}),
            properties.deviceSerialNumber({fromCurrent: true}),
            properties.questionnairePage(questionnairePage),
        ],
    });
};

const trackAdCleanMyDeviceClick = () => {
    track({
        event: gaEventNames.CLEAN_MY_DEVICE_BUTTON_CLICKED,
        properties: [properties.deviceModel({fromCurrent: true}), properties.deviceSerialNumber({fromCurrent: true})],
    });
};

const trackAdRemoveStickInstructionsScroll = () => {
    track({
        event: gaEventNames.REMOVE_STICK_INSTRUCTIONS_SCROLLED,
        properties: [properties.deviceModel({fromCurrent: true}), properties.deviceSerialNumber({fromCurrent: true})],
    });
};

const trackAdEntryPointScreenView = () => {
    track({
        event: gaEventNames.DEVICE_SCREEN_VIEWED,
        properties: [properties.deviceModel({fromCurrent: true}), properties.deviceSerialNumber({fromCurrent: true})],
    });
};

const trackAdInitialQuestionnaireScreenView = () => {
    const isQureMarket = appConfig.getIsQureMarket();
    const qureMarketValue = isQureMarket ? 'qure_market' : 'not_qure_market';

    track({
        event: gaEventNames.AD_INITIAL_QUESTIONNAIRE_VIEWED,
        properties: [
            properties.deviceModel({fromCurrent: true}),
            properties.deviceSerialNumber({fromCurrent: true}),
            {
                [analyticsEventProperties.TYPE]: qureMarketValue,
            },
        ],
    });
};

const trackAdFinishScreenView = () => {
    track({
        event: gaEventNames.AD_FINISH_SCREEN_VIEWED,
        properties: [properties.deviceModel({fromCurrent: true}), properties.deviceSerialNumber({fromCurrent: true})],
    });
};

const trackAdNoneOfTheAboveClick = () => {
    track({
        event: gaEventNames.AD_NONE_OF_THE_ABOVE_BUTTON_CLICKED,
        properties: [properties.deviceModel({fromCurrent: true}), properties.deviceSerialNumber({fromCurrent: true})],
    });
};

const trackHighlightsViewAllClick = () => {
    track({
        event: gaEventNames.HIGHLIGHT_VIEW_ALL_CLICKED,
        properties: [properties.pageId()],
    });
};

const trackConsumerProfileScreenView = () => {
    track({
        event: gaEventNames.PROFILE_SCREEN_VIEWED,
    });
};

const trackStartWithBluefyClick = (deviceType, deviceSerialNumber) => {
    track({
        event: gaEventNames.START_WITH_BLUEFY_CLICKED,
        properties: [properties.deviceModel({deviceType}), properties.deviceSerialNumber({deviceSerialNumber})],
    });
};

const trackAutoDeliveryBannerClick = () => {
    track({
        event: gaEventNames.AUTODELIVERY_INTERACTION,
        properties: [properties.interactionType(analyticsEventValues.BANNER_CLICKED)],
    });
};

const trackAutoDeliveryDashboardButtonClick = (trackingValue) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.AUTODELIVERY_DASHBOARD_BUTTON_CLICKED,
        properties: [properties.trackingValue(trackingValue)],
    });
};

const trackAutoDeliveryGetStartedScreen = () => {
    track({event: gaEventNames.AUTODELIVERY_GET_STARTED_SCREEN_VIEWED});
};

const trackAutoDeliveryGetStartedButtonClick = () => {
    track({event: gaEventNames.AUTODELIVERY_GET_STARTED_BUTTON_CLICKED});
};

const trackAutoDeliveryOrderScreen = (autoDeliveryStatus, orderSummary = null) => {
    track({
        event: gaEventNames.ORDER_SCREEN_VIEWED,
        properties: [
            {
                [analyticsEventProperties.AUTODELIVERY_STATUS]: autoDeliveryStatus,
            },
            getOrderSummaryProperties(orderSummary),
        ],
    });
};

const trackAutoDeliveryOrderSummaryLinkClick = (autoDeliveryStatus) => {
    track({
        event: gaEventNames.ORDER_SUMMARY_LINK_CLICKED,
        properties: [
            {
                [analyticsEventProperties.AUTODELIVERY_STATUS]: autoDeliveryStatus,
            },
        ],
    });
};

const trackAutoDeliveryDeliverAsapStarted = (orderSummary) => {
    track({
        event: gaEventNames.DELIVER_ASAP_STARTED,
        properties: [getOrderSummaryProperties(orderSummary)],
    });
};

const trackAutoDeliveryDeliverAsapCompleted = ({success, message, orderSummary}) => {
    track({
        event: gaEventNames.DELIVER_ASAP_COMPLETED,
        properties: [
            properties.success(success),
            {[analyticsEventProperties.MESSAGE]: message},
            getOrderSummaryProperties(orderSummary),
        ],
    });
};

const trackAutoDeliveryOrderButtonClick = (trackingValue, autoDeliveryStatus) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.ORDER_BUTTON_CLICKED,
        properties: [
            properties.trackingValue(trackingValue),
            {
                [analyticsEventProperties.AUTODELIVERY_STATUS]: autoDeliveryStatus,
            },
        ],
    });
};

const trackAutoDeliverySkipDeliveryNotAvailable = (orderSummary) => {
    track({
        event: gaEventNames.SKIP_DELIVERY_NOT_AVAILABLE,
        properties: [getOrderSummaryProperties(orderSummary)],
    });
};

const trackAutoDeliverySkipDeliveryStarted = (orderSummary) => {
    track({
        event: gaEventNames.SKIP_DELIVERY_STARTED,
        properties: [getOrderSummaryProperties(orderSummary)],
    });
};

const trackAutoDeliverySkipDeliveryCompleted = ({success, message, orderSummary}) => {
    track({
        event: gaEventNames.SKIP_DELIVERY_COMPLETED,
        properties: [
            properties.success(success),
            {[analyticsEventProperties.MESSAGE]: message},
            getOrderSummaryProperties(orderSummary),
        ],
    });
};

const getOrderSummaryProperties = (orderSummary) => {
    return orderSummary ? {[analyticsEventProperties.ORDER_SUMMARY]: orderSummary} : {};
};

const trackLogOutClick = () => {
    track({event: gaEventNames.LOG_OUT});
};

const trackSplashScreenViewed = () => {
    track({event: gaEventNames.WELCOME_SPLASH_SCREEN_VIEWED});
};

const trackAppUpdatePopupViewed = () => {
    track({event: gaEventNames.APP_UPDATE_POPUP_VIEWED});
};

const trackAppUpdateButtonClicked = () => {
    track({event: gaEventNames.APP_UPDATE_BUTTON_CLICKED});
};

const trackMaintenancePopupViewed = () => {
    track({event: gaEventNames.MAINTENANCE_POPUP_VIEWED});
};

const trackFeedbackPopupViewed = () => {
    track({event: gaEventNames.FEEDBACK_POPUP_VIEWED});
};

const trackFeedbackSubmitted = (isSuccess) => {
    track({event: gaEventNames.FEEDBACK_SUBMITTED, properties: [properties.success(isSuccess)]});
};

const trackBadgeDetailsClick = (trackingValue) => {
    if (!trackingValue) return;

    track({
        event: gaEventNames.BADGE_DETAILS_OPENED,
        properties: [properties.trackingValue(trackingValue), properties.pageId()],
    });
};

const trackViewAllButtonClick = () => {
    track({event: gaEventNames.ALL_BADGES_VIEWED});
};

export default {
    toggleAnalytics,
    trackEnableNotificationsScreenView,
    trackEnableNotificationsNextButtonClick,
    trackAllowPushNotifications,
    trackDisallowPushNotifications,
    trackHomeScreenView,
    trackHomeTopBannerClick,
    trackLoyaltyButtonClick,
    trackHighlightsBannerClick,
    trackForYouTileClick,
    trackShopScreenView,
    trackQuickOrderBannerClick,
    trackQuickOrderButtonClick,
    trackYourDevicesButtonClick,
    trackShopByProductBannerClick,
    trackDeviceScreenView,
    trackTryYourFirstIqosButtonClick,
    trackAboutMyDeviceScreenView,
    trackTipsAndTutorialsScreenView,
    trackUnregisterDeviceClick,
    trackInboxScreenView,
    trackInboxDetailsScreenView,
    trackInboxMessageButtonClick,
    trackActionScreen,
    trackPreferenceCenterScreenView,
    trackUpdateYourDataScreenView,
    trackContactUsScreenView,
    trackShowProfileLinkClick,
    trackTermsOfUseClick,
    trackPrivacyNoticeClick,
    trackAttributionNoticeClick,
    trackAddNewDeviceClick,
    trackPairDeviceClick,
    trackRenameDeviceScreenView,
    trackRenameDevice,
    trackAdQuestionnaireSelectClick,
    trackAdQuestionnaireScroll,
    trackAdCleanMyDeviceClick,
    trackAdRemoveStickInstructionsScroll,
    trackAdEntryPointScreenView,
    trackAdInitialQuestionnaireScreenView,
    trackAdFinishScreenView,
    trackAdNoneOfTheAboveClick,
    trackHighlightsViewAllClick,
    trackConsumerProfileScreenView,
    trackStartWithBluefyClick,
    trackAutoDeliveryBannerClick,
    trackAutoDeliveryDashboardButtonClick,
    trackAutoDeliveryGetStartedScreen,
    trackAutoDeliveryGetStartedButtonClick,
    trackAutoDeliveryOrderScreen,
    trackAutoDeliveryOrderSummaryLinkClick,
    trackAutoDeliveryDeliverAsapStarted,
    trackAutoDeliveryDeliverAsapCompleted,
    trackAutoDeliveryOrderButtonClick,
    trackAutoDeliverySkipDeliveryNotAvailable,
    trackAutoDeliverySkipDeliveryStarted,
    trackAutoDeliverySkipDeliveryCompleted,
    trackLogOutClick,
    trackSplashScreenViewed,
    trackAppUpdatePopupViewed,
    trackAppUpdateButtonClicked,
    trackMaintenancePopupViewed,
    trackFeedbackPopupViewed,
    trackFeedbackSubmitted,
    trackChromeDownloadButtonClick,
    trackPushNotificationsSettingScreenViewed,
    trackPushNotificationsSettingChanged,
    trackPushNotificationsPromptViewed,
    trackPushNotificationsSystemSettingsStepsViewed,
    trackViewAllButtonClick,
    trackBadgeDetailsClick,
};
